import { DeepPartial } from "react-hook-form";
import { usaStates } from "typed-usa-states";
import { z } from "zod";

export enum BaseballPlayerPosition {
  Catcher = "C",
  Pitcher = "P",
  FirstBase = "1B",
  ThirdBase = "3B",
  MiddleInfield = "MIF",
  OutField = "OF",
}

export const baseballPlayerPositionOptions: {
  value: BaseballPlayerPosition;
  label: string;
}[] = [
  {
    value: BaseballPlayerPosition.Catcher,
    label: "Catcher",
  },
  {
    value: BaseballPlayerPosition.Pitcher,
    label: "Pitcher",
  },
  {
    value: BaseballPlayerPosition.FirstBase,
    label: "1st Base",
  },
  {
    value: BaseballPlayerPosition.ThirdBase,
    label: "3rd Base",
  },
  {
    value: BaseballPlayerPosition.MiddleInfield,
    label: "Middle Infield",
  },
  {
    value: BaseballPlayerPosition.OutField,
    label: "Outfield",
  },
];

export const minGradClassYear = 2024;
export const maxGradClassYear = 2050;

const states: string[] = usaStates.map((state) => state.abbreviation);
const baseSchema = z.object({
  id: z.string().uuid().optional(),
  external_id: z.string().uuid().optional(),
  first_name: z.string(),
  last_name: z.string(),
  primary_position: z.nativeEnum(BaseballPlayerPosition),
  secondary_position: z.nativeEnum(BaseballPlayerPosition),
  grad_class: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z.coerce
      .number({ invalid_type_error: "Required" })
      .min(minGradClassYear)
      .max(maxGradClassYear),
  ),
  city: z.string(),
  state: z.string(),
  bats: z.enum(["Left", "Right", "Switch"]).optional(),
  throws: z.enum(["Left", "Right"]).optional(),
});

const addRefinements = (schema: typeof baseSchema) =>
  schema
    .refine((values) => values.primary_position !== values.secondary_position, {
      message: "Secondary position must be different than primary",
      path: ["secondary_position"],
    })
    .refine((values) => states.some((el) => el === values.state), {
      message: "Invalid state",
      path: ["state"],
    });

export const playerFormSchema = addRefinements(baseSchema);

export const baseballPlayerFormSchema = addRefinements(
  baseSchema.extend({
    bats: z.enum(["Left", "Right", "Switch"]).optional(),
    throws: z.enum(["Left", "Right"]).optional(),
  }),
);
export type PlayerFormValues = z.infer<typeof playerFormSchema>;

export const playerFormDefaultValues: DeepPartial<PlayerFormValues> = {
  grad_class: minGradClassYear,
  primary_position: BaseballPlayerPosition.Catcher,
  secondary_position: BaseballPlayerPosition.Pitcher,
};
